import { useMemo } from 'react';

export interface RequiredFieldSpec<TValues> {
  name: keyof TValues & string;
  label: string;
  isSatisfied?: (values: TValues) => boolean;
}

const defaultSatisfied = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  return !!value;
};

const joinNaturally = (labels: string[]): string | null => {
  if (labels.length === 0) return null;
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

export function useStillNeeded<TValues extends Record<string, unknown>>(
  values: TValues,
  requiredFields: ReadonlyArray<RequiredFieldSpec<TValues>>,
): { missing: string[]; phrase: string | null } {
  return useMemo(() => {
    const missing = requiredFields
      .filter((spec) =>
        spec.isSatisfied
          ? !spec.isSatisfied(values)
          : !defaultSatisfied(values[spec.name]),
      )
      .map((spec) => spec.label);
    return { missing, phrase: joinNaturally(missing) };
  }, [values, requiredFields]);
}
